import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../components/ConfigContext';
import ForwardToInboxIcon from '../../public/forward_to_inbox.svg';
import DefaultProductImage from '../../public/StudioConCON.svg';
import Cookies from 'js-cookie';
import { Box, Card, CardContent, Typography, Link as MuiLink, CircularProgress } from '@mui/material';
import { constants, ROUTES } from '../constants';
import { useApplicationsList } from '@/components/ApplicationContext';
import { PermissionGate } from '@/components/PermissionGate';
import { ADMIN_ACCESS_ACTIONS } from '@/utils/permissionGateConfig';
import authenticatedRoute from '@/views/AuthenticatedRoute';
import { cleanupAllScopes } from './Product';
import { useThemeDetails } from '../components/ThemeDetailsContext';
import PlatformAdministration from '../../public/PlatformAdministration.svg';
const handleTruncationTooltip = (e, text) => {
    const el = e.currentTarget;
    el.title = el.scrollHeight > el.clientHeight ? text : '';
};
const ProductCard = ({ product, onClick }) => {
    const { configState } = useContext(ConfigContext);
    const isUnavailable = (product?.details?.applicationState || '')?.toLowerCase() !== 'active';
    const hasDescription = product?.details?.applicationDescription &&
        product?.details?.applicationDescription.trim().length > 0;
    const buttonColor = configState?.settings?.button_color || '#26890D';
    const handleClick = () => {
        if (isUnavailable)
            return;
        onClick(product?.id, product?.details?.productName ?? '');
    };
    const handleDescriptionTranslation = (product, key) => {
        const user = Cookies.get('user');
        if (user) {
            try {
                const selected_language = JSON.parse(user).details?.selected_language || 'en';
                if (selected_language && selected_language !== 'en') {
                    if (key === 'desc') {
                        const description = `description_${selected_language}`;
                        return (product?.details?.[description] ||
                            product?.description);
                    }
                    else {
                        const description = `applicationDescription_${selected_language}`;
                        return (product?.details?.[description] ||
                            product?.details?.applicationDescription);
                    }
                }
            }
            catch (error) {
                // If JSON parsing fails, fall through to return default
                console.error('Error parsing user cookie:', error);
            }
        }
        return key === 'desc'
            ? product?.description
            : product?.details?.applicationDescription;
    };
    return (_jsxs(Card, { sx: {
            width: '100%',
            height: {
                xs: 'auto',
                sm: hasDescription ? '21.5rem' : '16rem'
            },
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: isUnavailable ? '#F8F8F8' : '#FFFFFF',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            boxShadow: 'none',
            cursor: isUnavailable ? 'not-allowed' : 'pointer',
            '&:hover': isUnavailable
                ? {}
                : {
                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.25)',
                    '& .image-box': {
                        maxHeight: { xs: '5.75rem', sm: '7.5625rem' },
                        minHeight: { xs: '5.75rem', sm: '7.5625rem' }
                    },
                    '& .image-box::after': { opacity: 0 }
                }
        }, onClick: handleClick, children: [_jsx(Box, { className: "image-box", sx: {
                    maxHeight: isUnavailable
                        ? '5rem'
                        : { xs: '6.5rem', sm: '8.5rem' },
                    minHeight: isUnavailable
                        ? '5rem'
                        : { xs: '6.5rem', sm: '8.5rem' },
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: buttonColor === '#26890D' ? '#F5F5F5' : buttonColor,
                        opacity: 0.6,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'none',
                        borderRadius: '8px 8px 0 0'
                    }
                }, children: _jsx("img", { src: product?.details?.image_base64 || DefaultProductImage, alt: `${product?.name} logo`, style: {
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover'
                    } }) }), _jsxs(CardContent, { sx: {
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: '16px !important',
                    gap: 1.5
                }, children: [_jsx(Typography, { variant: "h6", color: isUnavailable ? '#595959' : '#121212', fontWeight: 600, sx: { mb: 0.5 }, children: product?.name }), _jsxs(Typography, { variant: "body2", color: "#545454ff", component: "div", sx: {
                            minHeight: 40
                        }, children: [_jsx(Box, { component: "div", sx: {
                                    fontWeight: 800,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                }, onMouseEnter: (e) => handleTruncationTooltip(e, handleDescriptionTranslation(product, 'desc') || ''), children: handleDescriptionTranslation(product, 'desc') }), _jsx(Box, { component: "div", sx: {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: 'vertical'
                                }, onMouseEnter: (e) => handleTruncationTooltip(e, handleDescriptionTranslation(product, 'appDesc') || ''), children: handleDescriptionTranslation(product, 'appDesc') })] }), isUnavailable && (_jsxs(Box, { sx: {
                            mt: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }, children: [_jsx(Box, { sx: {
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 999,
                                    bgcolor: '#EBEBEB',
                                    color: '#45484bff',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    lineHeight: 1
                                }, children: constants.unavailable }), _jsxs(MuiLink, { href: `mailto:${product?.details?.contactEmail}`, underline: "none", sx: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    color: '#3C4043',
                                    fontWeight: 600,
                                    '&:hover': { textDecoration: 'underline' }
                                }, onClick: (e) => e.stopPropagation(), children: [_jsx("img", { src: ForwardToInboxIcon, alt: "Forward to inbox", style: { width: 18, height: 18 } }), _jsx("span", { children: constants.email_your_admin })] })] }))] })] }));
};
const AdministrationCard = () => {
    const navigate = useNavigate();
    const { configState } = useContext(ConfigContext);
    const buttonColor = configState?.settings?.button_color || '#26890D';
    return (_jsxs(Card, { sx: {
            width: '100%',
            height: { xs: 'auto', sm: '21.5rem' },
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFFFFF',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            boxShadow: 'none',
            cursor: 'pointer',
            '&:hover': {
                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.25)',
                '& .image-box': {
                    maxHeight: { xs: '5.75rem', sm: '7.5625rem' },
                    minHeight: { xs: '5.75rem', sm: '7.5625rem' }
                },
                '& .image-box::after': { opacity: 0 }
            }
        }, onClick: () => navigate(ROUTES.SETTINGS), children: [_jsx(Box, { className: "image-box", sx: {
                    maxHeight: { xs: '6.5rem', sm: '8.5rem' },
                    minHeight: { xs: '6.5rem', sm: '8.5rem' },
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: buttonColor === '#26890D' ? '#F5F5F5' : buttonColor,
                        opacity: 0.6,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'none',
                        borderRadius: '8px 8px 0 0'
                    }
                }, children: _jsx("img", { src: PlatformAdministration, alt: `Administration logo`, style: {
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover'
                    } }) }), _jsxs(CardContent, { sx: {
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: '16px !important',
                    gap: 1.5
                }, children: [_jsx(Typography, { variant: "h6", color: '#121212', fontWeight: 600, sx: { mb: 0.5 }, children: "Administration" }), _jsxs(Typography, { variant: "body2", color: "#545454ff", component: "div", sx: {
                            minHeight: 40
                        }, children: [_jsx(Box, { component: "div", sx: {
                                    fontWeight: 800,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                }, onMouseEnter: (e) => handleTruncationTooltip(e, constants.administration_subtitle), children: constants.administration_subtitle }), _jsx(Box, { component: "div", sx: {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: 'vertical'
                                }, onMouseEnter: (e) => handleTruncationTooltip(e, constants.administration_description), children: constants.administration_description })] })] })] }));
};
const ProductSelection = () => {
    const { configState, setConfigState } = useContext(ConfigContext);
    const { stateImages } = useThemeDetails();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const getApplications = useApplicationsList();
    const navigate = useNavigate();
    useEffect(() => {
        const loadApplications = async () => {
            try {
                setLoading(true);
                const apps = await getApplications();
                setProducts(apps || []);
                const currentApp = apps.find((app) => app?.details?.baseApp === 'true');
                if (currentApp) {
                    setConfigState({
                        ...configState,
                        selectedProduct: String(currentApp.id)
                    });
                    sessionStorage.setItem('current_application', JSON.stringify(currentApp));
                    Cookies.set('Product Selected', String(currentApp.id), {
                        secure: true,
                        sameSite: 'Strict'
                    });
                }
            }
            catch (error) {
                console.error('Error fetching applications:', error);
            }
            finally {
                setLoading(false);
            }
        };
        loadApplications();
        Cookies.remove('Product Selected');
        sessionStorage.removeItem('current_application');
        globalThis.dispatchEvent(new CustomEvent('current_application_changed'));
        cleanupAllScopes();
    }, []);
    const handleProductClick = (productId, productSlug) => {
        const currentApp = products.find((app) => app.id === productId);
        const productUrl = currentApp?.details?.productUrl;
        const isJupyter = productSlug?.toLowerCase()?.includes('jupyter');
        const isSuperset = productSlug?.toLowerCase()?.includes('superset');
        if (isJupyter) {
            if (productUrl) {
                window.open(productUrl, '_blank', 'noopener,noreferrer');
            }
            else {
                console.error(`No productUrl found for product "${productSlug}"`);
            }
            return;
        }
        if (isSuperset) {
            if (productUrl) {
                window.open(productUrl, '_blank', 'noopener,noreferrer');
            }
            else {
                console.error(`No productUrl found for product "${productSlug}"`);
            }
            return;
        }
        setConfigState({ ...configState, selectedProduct: String(productId) });
        Cookies.set('Product Selected', String(productId), {
            secure: true,
            sameSite: 'Strict'
        });
        if (currentApp) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { image_base64, ...detailsWithoutImage } = currentApp.details || {};
            const appToStore = {
                ...currentApp,
                details: detailsWithoutImage
            };
            sessionStorage.setItem('current_application', JSON.stringify(appToStore));
        }
        navigate(`/${productSlug}?id=${productId}`);
    };
    const sortedProducts = useMemo(() => {
        return [...products]
            .filter((app) => app?.details?.purpose?.toLowerCase() !== 'internal')
            .sort((a, b) => {
            // First, sort by active status (active cards first)
            const isActiveA = (a?.details?.applicationState || '').toLowerCase() ===
                'active';
            const isActiveB = (b?.details?.applicationState || '').toLowerCase() ===
                'active';
            if (isActiveA && !isActiveB)
                return -1;
            if (!isActiveA && isActiveB)
                return 1;
            // Then sort alphabetically within each group
            const nameA = a?.name || '';
            const nameB = b?.name || '';
            return nameA.localeCompare(nameB);
        });
    }, [products]);
    if (loading) {
        return (_jsx(Box, { sx: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 'calc(100vh - 60px)',
                overflow: 'hidden'
            }, children: stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, { size: 60, sx: { color: configState?.settings?.button_color } })) }));
    }
    return (_jsx(Box, { children: _jsxs(Box, { sx: {
                display: 'grid',
                gap: 2,
                px: 2,
                pb: 2,
                pt: 2,
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fill, minmax(22rem, 22rem))'
                },
                justifyContent: 'center'
            }, children: [sortedProducts?.map((p) => (_jsx(Box, { children: _jsx(ProductCard, { product: p, onClick: handleProductClick }) }, p.id))), _jsx(PermissionGate, { action: ADMIN_ACCESS_ACTIONS, children: _jsx(AdministrationCard, {}) })] }) }));
};
export default authenticatedRoute(ProductSelection);
